import {defineMessages, injectIntl} from 'react-intl'
import _ from 'lodash'

// utility component for re-using a message in multiple places without
// having to injectIntl in the parent
export const Msg = injectIntl(({m, capitalized, values, intl}) => (
  capitalized
    ? _.capitalize(intl.formatMessage(m, values))
    : intl.formatMessage(m, values)
))

export const featureNames = defineMessages({
  goals: {
    id: 'featureNames.goals',
    defaultMessage: 'Goals',
    description: 'Feature name for Goals feature'
  },
  find: {
    id: 'featureNames.find',
    defaultMessage: 'Find',
    description: 'Feature name for the Find search page, used to search for bike rides'
  },
  routePlanner: {
    id: 'featureNames.routePlanner',
    defaultMessage: 'Route Planner',
    description: 'Feature name for the Route Planner tool, used to plan bike rides'
  },
  rideReports: {
    id: 'featureNames.rideReports',
    defaultMessage: 'Ride Reports',
    description: 'Feature name for the Ride Reports page, a showcase of user-written blog posts about interesting bike rides'
  },
  help: {
    id: 'featureNames.help',
    defaultMessage: 'Help',
    description: 'Feature name for our Help portal, a comprehensive documentation resource for how to use the site'
  },
  dashboard: {
    id: 'featureNames.dashboard',
    defaultMessage: 'Dashboard',
    description: "Feature name for our user Dashboard, an at-a-glance look at all of a user's data on the site"
  },
  calendar: {
    id: 'featureNames.calendar',
    defaultMessage: 'Calendar',
    description: "Feature name for our user Calendar, a calendar view of a user's rides and events"
  },
  routes: {
    id: 'featureNames.routes',
    defaultMessage: 'Routes',
    description: "Feature name for our user Routes page, a searchable list of all of a user's planned and saved cycling routes"
  },
  rides: {
    id: 'featureNames.rides',
    defaultMessage: 'Rides',
    description: "Feature name for our user Rides page, a searchable list of all of a user's recorded cycling rides"
  },
  collections: {
    id: 'featureNames.collections',
    defaultMessage: 'Collections',
    description: 'Feature name for our user Collections page, a place where users can organize their routes and rides'
  },
  analyze: {
    id: 'featureNames.analyze',
    defaultMessage: 'Analyze',
    description: 'Feature name for our Analyze page, a filterable and customizable view of all user activity'
  },
  upload: {
    id: 'featureNames.upload',
    defaultMessage: 'Upload',
    description: 'Page used to upload ride data into our site'
  },
  friends: {
    id: 'featureNames.friends',
    defaultMessage: 'Friends',
    description: 'Feature name for our Friends page, a feed of all friended user activity'
  },
  more: {
    id: 'featureNames.more',
    defaultMessage: 'More',
    description: 'Feature name for our More page, which contains various additional functionality not found on other pages'
  },
  features: {
    id: 'featureNames.features',
    defaultMessage: 'Features',
    description: 'Page descibing the features available on the Ride with GPS platform'
  },
  upgrade: {
    id: 'featureNames.upgrade',
    defaultMessage: 'Upgrade',
    description: 'Page which allows a user to upgrade their account'
  },
  organizations: {
    id: 'featureNames.organizations',
    defaultMessage: 'Organizations',
    description: 'Page with information about our Organizations accounts'
  },
  journal: {
    id: 'featureNames.journal',
    defaultMessage: 'Journal',
    description: 'Page with dispatches from inside the company and our partners'
  },
  newsAndPress: {
    id: 'featureNames.newsAndPress',
    defaultMessage: 'News & Press',
    description: 'Page with dispatches from inside the company and our partners'
  }
})

export const commonNouns = defineMessages({
  goal: {
    id: 'commonNouns.goal',
    defaultMessage: 'goal',
    description: 'a user-created cycling goal (i.e. to ride more miles, do more elevation gain, etc)'
  },
  ride: {
    id: 'commonNouns.ride',
    defaultMessage: 'ride',
    description: 'a user-uploaded bicycle ride recording'
  },
  trip: {
    id: 'commonNouns.trip',
    defaultMessage: 'trip',
    description: 'a user-uploaded bicycle trip recording'
  },
  route: {
    id: 'commonNouns.route',
    defaultMessage: 'route',
    description: 'a user-created bicycle route, planned using the route planner'
  },
  date: {
    id: 'commonNouns.date',
    defaultMessage: 'date',
    description: 'a specific calendar date'
  },
  distance: {
    id: 'commonNouns.distance',
    defaultMessage: 'distance',
    description: 'a distance value, either in miles or kilometers'
  },
  elevation: {
    id: 'commonNouns.elevation',
    defaultMessage: 'elevation',
    description: 'an elevation value, either in meters or feet'
  },
  movingTime: {
    id: 'commonNouns.movingTime',
    defaultMessage: 'moving time',
    description: 'Duration that a user was moving during a bike rides, in hours/minuts/seconds'
  },
  item: {
    id: 'commonNouns.item',
    defaultMessage: '{count, plural, =0 {0 items} one {# item} other {# items}}'
  },
  title: {
    id: 'commonNouns.title',
    defaultMessage: 'title',
    description: 'A name for something created by a user'
  },
  description: {
    id: 'commonNouns.description',
    defaultMessage: 'description',
    description: 'Text explaining or giving an account of something'
  },
  overview: {
    id: 'commonNouns.overview',
    defaultMessage: 'Overview',
    description: 'A general summary of something'
  },
  image: {
    id: 'commonNouns.image',
    defaultMessage: 'image',
    description: 'Visual representation of something, a photo'
  },
  stats: {
    id: 'commonNouns.stats',
    defaultMessage: 'stats',
    description: 'Numerical information. Statistics.'
  }
})

export const commonVerbs = defineMessages({
  save: {
    id: 'commonVerbs.save',
    defaultMessage: 'Save',
    description: 'The act of storing information'
  },
  edit: {
    id: 'commonVerbs.edit',
    defaultMessage: 'Edit',
    description: 'The act of changing information'
  },
  delete: {
    id: 'commonVerbs.delete',
    defaultMessage: 'Delete',
    description: 'To remove or destroy, especially information'
  },
  import: {
    id: 'commonVerbs.import',
    defaultMessage: 'Import',
    description: 'The act of bringing something in'
  },
  add: {
    id: 'commonVerbs.add',
    defaultMessage: 'Add',
    description: 'To join to something else'
  }
})

export const goalTypeLabels = defineMessages({
  distance: {
    id: 'goalTypeLabels.distance',
    defaultMessage: 'Distance',
    description: 'Label for a user-created Distance goal'
  },
  moving_time: {
    id: 'goalTypeLabels.moving_time',
    defaultMessage: 'Moving Time',
    description: 'Label for a user-created Moving Time goal'
  },
  elevation_gain: {
    id: 'goalTypeLabels.elevation_gain',
    defaultMessage: 'Elevation',
    description: 'Label for a user-created Elevation Gain goal'
  },
  leaderboard: {
    id: 'goalTypeLabels.leaderboard',
    defaultMessage: 'Leaderboard',
    description: 'Label for a user-created Leaderboard goal, where the user competes with friends for a given milestone'
  }
})

export const commonDateStrings = defineMessages({
  startDate: {
    id: 'commonDateStrings.startDate',
    defaultMessage: 'Start Date',
    description: 'String for identifying the start date of an event or goal'
  },
  endDate: {
    id: 'commonDateStrings.endDate',
    defaultMessage: 'End Date',
    description: 'String for identifying the end date of an event or goal'
  }
})
